import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Logo } from "./Logo";
import { getEnv } from "@/lib/env";
import { routes } from "@/shared/constants";
// OJO: importar `ME` del módulo de queries (solo usa `gql`), NO del barril
// `@/features/auth` — cualquier reexportación que arrastre `@/lib/apollo/client`
// resuelve VITE_GATEWAY_URL en el import y revienta en CI (sin esa env). Desde
// sesion-viva-03 la renovación (`@/lib/auth/renovacion`) resuelve la env en cada
// llamada, así que el barril ya no arrastra ese fallo por `useRefreshAuth`; el
// import directo se mantiene porque el cliente Apollo sigue siendo así.
import { ME } from "@/features/auth/api/queries";
import type { Me } from "@/entities";
import { usePortalSession } from "@/features/releases/usePortalSession";
import {
  useLandingContent,
  useLandingContentStore,
} from "@/features/admin/content/landingContent";

const nav = [
  { label: "Tipos de negocio", href: "/#tipos" },
  { label: "Ecosistema", href: "/#ecosistema" },
  { label: "Hardware", href: "/#hardware" },
  { label: "Precios", href: "/#precios" },
  { label: "Lo último", href: "/#novedades" },
];

/**
 * Entrada del menú. Las cinco de arriba son ANCLAS de la portada y se pintan
 * con `<a href>`; "Descargas" es una RUTA propia y se pinta con `<Link to>`,
 * para que no recargue el sitio entero al pulsarla.
 */
type NavItem = { label: string; href: string } | { label: string; to: string };

const esRuta = (n: NavItem): n is { label: string; to: string } => "to" in n;

interface MeData {
  me: Me | null;
}

/** Avatar del usuario: su foto si la tiene, si no las iniciales. */
function ProfileAvatar({ me }: { me: Me | null }) {
  if (me?.photoPath) {
    return (
      <img
        src={me.photoPath}
        alt=""
        width={28}
        height={28}
        className="size-7 rounded-full border border-black/10 object-cover"
      />
    );
  }
  const initials = (
    (me?.firstName?.charAt(0) ??
      me?.username?.charAt(0) ??
      me?.email?.charAt(0) ??
      "U") + (me?.lastName?.charAt(0) ?? "")
  ).toUpperCase();
  return (
    <span className="grid size-7 place-items-center rounded-full bg-gradient-to-br from-orange-400 to-red-500 text-[11px] font-bold text-white">
      {initials}
    </span>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { hidden } = useLandingContent();
  const cmsReady = useLandingContentStore((s) => s.status) === "ready";

  // Sesión SSO en página pública: hidrata el token en diferido (single-flight,
  // compartido con el boot de AppProviders). Con sesión mostramos "Mi Perfil";
  // el perfil (foto/nombre) solo se consulta cuando hay token — nunca para
  // visitantes anónimos.
  const { isLoggedIn, ensureSession } = usePortalSession();
  useEffect(() => {
    void ensureSession();
  }, [ensureSession]);
  const { data } = useQuery<MeData>(ME, {
    skip: !isLoggedIn,
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  const me = data?.me ?? null;

  // El enlace a la sección de comunidad solo existe si la env está configurada
  // (sin VITE_COMUNIDAD_URL la sección no se renderiza — sin anclas muertas).
  // Va justo después de "Hardware" (o en su lugar, si Hardware está oculta
  // desde la administración: hidden.hardware).
  const navItems: NavItem[] = nav.flatMap((n) => {
    if (n.href !== "/#hardware") return [n];
    const items = hidden?.hardware ? [] : [n];
    if (getEnv("VITE_COMUNIDAD_URL")) {
      items.push({ label: "Comunidad", href: "/#comunidad" });
    }
    return items;
  });

  // "Descargas" va al FINAL, y solo cuando hay contenido REAL del CMS que diga
  // que está visible. Las otras cinco entradas son anclas de esta misma página
  // y no estorban mientras carga; ésta es una ruta a una página que, sin CMS,
  // solo sabe decir que no está disponible — sería un camino muerto. Mismo
  // criterio que el pie (`showsSection` en `footerLinks.ts`), para que el menú
  // y el pie no se contradigan en la misma pantalla.
  if (cmsReady && !hidden?.downloads) {
    navItems.push({ label: "Descargas", to: routes.public.downloads });
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-6 h-16 lg:h-20 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Link to="/" className="flex items-center"><Logo showWordmark={false} /></Link>
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-muted-foreground">
            {navItems.map((n) =>
              esRuta(n) ? (
                <Link key={n.to} to={n.to} className="hover:text-foreground transition-colors">
                  {n.label}
                </Link>
              ) : (
                <a key={n.href} href={n.href} className="hover:text-foreground transition-colors">
                  {n.label}
                </a>
              ),
            )}
          </nav>
        </div>

        <div className="hidden lg:flex items-center gap-2">
          {isLoggedIn ? (
            <Link
              to={routes.app.dashboard}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 py-1.5 pl-1.5 pr-4 text-sm font-semibold hover:bg-white transition-all"
            >
              <ProfileAvatar me={me} />
              Mi Perfil
            </Link>
          ) : (
            <>
              <Link to="/login" className="px-3 py-2 text-sm font-medium hover:text-primary transition-colors">
                Iniciar sesión
              </Link>
              <Link to="/registro" className="px-5 py-2.5 bg-foreground text-background text-sm font-semibold rounded-full hover:bg-neutral-800 transition-all">
                Comenzar ahora
              </Link>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          {isLoggedIn ? (
            <Link
              to={routes.app.dashboard}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 py-1 pl-1 pr-3 text-xs font-semibold"
            >
              <ProfileAvatar me={me} />
              Mi Perfil
            </Link>
          ) : (
            <Link to="/registro" className="px-4 py-2 bg-foreground text-background text-xs font-semibold rounded-full">
              Comenzar
            </Link>
          )}
          <button
            aria-label="Abrir menú"
            onClick={() => setOpen((v) => !v)}
            className="size-10 grid place-items-center rounded-full hover:bg-neutral-100"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white">
          <nav className="px-5 py-4 flex flex-col">
            {navItems.map((n) =>
              esRuta(n) ? (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base font-medium border-b border-black/5 flex items-center justify-between"
                >
                  {n.label}
                  <span className="text-muted-foreground">→</span>
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="py-3 text-base font-medium border-b border-black/5 flex items-center justify-between"
                >
                  {n.label}
                  <span className="text-muted-foreground">→</span>
                </a>
              ),
            )}
            <div className="flex items-center gap-3 pt-5">
              {isLoggedIn ? (
                <Link
                  to={routes.app.dashboard}
                  onClick={() => setOpen(false)}
                  className="flex-1 px-4 py-3 rounded-full bg-foreground text-background text-sm font-semibold text-center"
                >
                  Mi Perfil
                </Link>
              ) : (
                <Link to="/login" className="flex-1 px-4 py-3 rounded-full border border-black/10 text-sm font-semibold text-center">
                  Iniciar sesión
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
